'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface Payment {
  id: string;
  order_id: string | null;
  order_number: string | null;
  amount: number;
  currency: string;
  payment_method: string;
  payment_gateway: string | null;
  gateway_payment_id: string | null;
  payment_status: string;
  failure_reason: string | null;
  refund_amount: number;
  refund_reason: string | null;
  refunded_at: string | null;
  paid_at: string | null;
  created_at: string;
}

const PAYMENT_STATUS_CONFIG: Record<string, { label: string; cls: string; icon: string }> = {
  pending:              { label: 'Pending',             cls: 'bg-amber-100 text-amber-700',   icon: 'ClockIcon' },
  processing:           { label: 'Processing',          cls: 'bg-blue-100 text-blue-700',     icon: 'ArrowPathIcon' },
  paid:                 { label: 'Paid',                cls: 'bg-green-100 text-green-700',   icon: 'CheckCircleIcon' },
  failed:               { label: 'Failed',              cls: 'bg-red-100 text-red-700',       icon: 'XCircleIcon' },
  refunded:             { label: 'Refunded',            cls: 'bg-purple-100 text-purple-700', icon: 'ArrowUturnLeftIcon' },
  partially_refunded:   { label: 'Partial Refund',      cls: 'bg-orange-100 text-orange-700', icon: 'ArrowUturnLeftIcon' },
};

const METHOD_LABELS: Record<string, string> = {
  cod:        'Cash on Delivery',
  upi:        'UPI',
  card:       'Card',
  netbanking: 'Net Banking',
  wallet:     'Wallet',
};

function formatINR(paise: number) {
  return `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function CustomerPaymentsPage() {
  const router = useRouter();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchPayments = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from('customer_payments')
      .select('id, order_id, order_number, amount, currency, payment_method, payment_gateway, gateway_payment_id, payment_status, failure_reason, refund_amount, refund_reason, refunded_at, paid_at, created_at')
      .eq('customer_id', uid)
      .order('created_at', { ascending: false });
    setPayments(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }
      fetchPayments(user.id);
    });
  }, [router, fetchPayments]);

  const filtered = payments.filter(p => {
    const matchFilter = filter === 'all' || p.payment_status === filter;
    const matchSearch = !search ||
      (p.order_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.gateway_payment_id || '').toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const totalPaid = payments.filter(p => p.payment_status === 'paid').reduce((s, p) => s + p.amount, 0);
  const totalRefunded = payments.filter(p => ['refunded', 'partially_refunded'].includes(p.payment_status)).reduce((s, p) => s + (p.refund_amount || 0), 0);
  const pendingCount = payments.filter(p => p.payment_status === 'pending').length;

  return (
    <CustomerDashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payment History</h1>
          <p className="text-sm text-slate-500 mt-0.5">Track all your payment transactions</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Payments', value: payments.length, icon: 'DocumentTextIcon', cls: 'bg-blue-50 text-blue-600' },
            { label: 'Total Paid', value: formatINR(totalPaid), icon: 'CheckCircleIcon', cls: 'bg-green-50 text-green-600' },
            { label: 'Total Refunded', value: formatINR(totalRefunded), icon: 'ArrowUturnLeftIcon', cls: 'bg-purple-50 text-purple-600' },
            { label: 'Pending', value: pendingCount, icon: 'ClockIcon', cls: 'bg-amber-50 text-amber-600' },
          ].map(card => (
            <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${card.cls}`}>
                <Icon name={card.icon as never} size={18} />
              </div>
              <p className="text-xl font-bold text-slate-900">{card.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by order # or transaction ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'paid', 'pending', 'processing', 'failed', 'refunded'].map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filter === s ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
        </div>

        {/* Payments List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
              <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Icon name="CreditCardIcon" size={24} className="text-slate-300" />
              </div>
              <p className="text-sm text-slate-400">No payment records found</p>
              <p className="text-xs text-slate-300 mt-1">Your payment history will appear here after placing orders</p>
            </div>
          ) : (
            filtered.map(payment => {
              const cfg = PAYMENT_STATUS_CONFIG[payment.payment_status] || { label: payment.payment_status, cls: 'bg-slate-100 text-slate-600', icon: 'QuestionMarkCircleIcon' };
              const isExpanded = expanded === payment.id;
              return (
                <div key={payment.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div
                    className="px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpanded(isExpanded ? null : payment.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.cls}`}>
                        <Icon name={cfg.icon as never} size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-900">
                            {payment.order_number ? `Order #${payment.order_number}` : 'Payment'}
                          </p>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
                            {cfg.label}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {METHOD_LABELS[payment.payment_method] || payment.payment_method}
                          {payment.payment_gateway ? ` · ${payment.payment_gateway}` : ''}
                          {' · '}{formatDate(payment.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-base font-bold text-slate-900">{formatINR(payment.amount)}</span>
                      <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} className="text-slate-400" />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-slate-400">Payment Method</p>
                          <p className="font-medium text-slate-800 mt-0.5">{METHOD_LABELS[payment.payment_method] || payment.payment_method}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Amount</p>
                          <p className="font-medium text-slate-800 mt-0.5">{formatINR(payment.amount)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Status</p>
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full mt-0.5 ${cfg.cls}`}>
                            <Icon name={cfg.icon as never} size={12} />
                            {cfg.label}
                          </span>
                        </div>
                        {payment.paid_at && (
                          <div>
                            <p className="text-xs text-slate-400">Paid At</p>
                            <p className="font-medium text-slate-800 mt-0.5">{formatDateTime(payment.paid_at)}</p>
                          </div>
                        )}
                        {payment.gateway_payment_id && (
                          <div className="col-span-2">
                            <p className="text-xs text-slate-400">Transaction ID</p>
                            <p className="font-mono text-xs text-slate-700 mt-0.5 break-all">{payment.gateway_payment_id}</p>
                          </div>
                        )}
                        {payment.failure_reason && (
                          <div className="col-span-2 sm:col-span-3">
                            <p className="text-xs text-slate-400">Failure Reason</p>
                            <p className="text-xs text-red-600 mt-0.5">{payment.failure_reason}</p>
                          </div>
                        )}
                        {(payment.refund_amount > 0) && (
                          <>
                            <div>
                              <p className="text-xs text-slate-400">Refund Amount</p>
                              <p className="font-medium text-purple-700 mt-0.5">{formatINR(payment.refund_amount)}</p>
                            </div>
                            {payment.refunded_at && (
                              <div>
                                <p className="text-xs text-slate-400">Refunded At</p>
                                <p className="font-medium text-slate-800 mt-0.5">{formatDateTime(payment.refunded_at)}</p>
                              </div>
                            )}
                            {payment.refund_reason && (
                              <div className="col-span-2 sm:col-span-3">
                                <p className="text-xs text-slate-400">Refund Reason</p>
                                <p className="text-xs text-slate-700 mt-0.5">{payment.refund_reason}</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </CustomerDashboardLayout>
  );
}
